import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, map, pipe } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { resolve } from './resolve';
import { cloneDeep as _cloneDeep, merge as _merge } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class ResourceService {
  private path!: string;
  private authorizationToken: string = '';
  private queryParams: { [k: string]: string | string[] } = {};
  private headerParams: { [k: string]: string | string[] } = {};

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  urlPath(pathString : string){
    this.path = pathString;
    return this;
  }

  authorization(type: 'bearer' | 'basic' | string, token: string): ResourceService {
    type = type[0].toUpperCase() + type.substr(1).toLowerCase();
    this.authorizationToken = `${type} ${token}`;
    return this;
  }

  basic(userOrToken: string, password?: string): ResourceService {
    if (password === undefined) return this.authorization('basic', userOrToken);
    else return this.authorization('basic', btoa(`${userOrToken}:${password}`));
  }

  bearer(token: string): ResourceService {
    return this.authorization('bearer', token);
  }

  delete(): Observable<any> {
    return this.http.delete(this.makeUrl(), { headers: this.makeHeaders() });
  }

  get<T>(responseType?: any): Observable<T> | Observable<string> | Observable<Blob> | Observable<ArrayBuffer> {
    const headers = this.makeHeaders(),
      opts = { headers };

    return this.http.get(this.makeUrl(), { ...opts, responseType }) as Observable<T>;
  }

  head(): Observable<any> {
    return this.http.head(this.makeUrl(), { headers: this.makeHeaders() });
  }

  headers(headers: { [p: string]: string | string[] }): ResourceService {
    this.headerParams = _cloneDeep(headers);
    return this;
  }

  params(p: { [p: string]: string | string[] }): ResourceService {
    this.queryParams = _cloneDeep(p);
    return this;
  }

  post<T>(body?: any, options: { observe: 'response' | 'body' | 'blob' } = { observe: 'body' }): Observable<T | HttpResponse<T>> {
    return this.http
      .post<T>(this.makeUrl(), body, { headers: this.makeHeaders(), observe: 'response' })
      .pipe(
        map(response => {
          if (options.observe == 'body') return response.body as T;
          else return response;
        })
      );
  }

  postBlob(body?: any): Observable<HttpResponse<Blob>> {
    return this.http.post(
      this.makeUrl(),
      body,
      { headers: this.makeHeaders(), observe: 'response', responseType: 'blob' }) as Observable<HttpResponse<Blob>>;
  }

  put<T>(body?: any): Observable<T> {
    return this.http.put<T>(this.makeUrl(), body, { headers: this.makeHeaders() });
  }

  resolve(): string {
    return this.makeUrl();
  }

  private makeUrl() {
    return resolve(this.path, _merge({}, this.queryParams));
  }

  private makeHeaders(): HttpHeaders {
    if (!this.headerParams) return new HttpHeaders();
    const headers = Object
      .keys(this.headerParams)
      .reduce((h, k) => {
        return h.append(k, this.headerParams[k]);
      }, new HttpHeaders());
    if (this.authorizationToken) return headers.append('Authorization', this.authorizationToken);
    else return headers;
  }
}
